import { deepMerge, type JSONSerializable } from './JSONSerializable';

type PathSegment =
  | { kind: 'key'; name: string }
  | { kind: 'keyWildcard'; prefix: string }
  | { kind: 'arrayAll' }
  | { kind: 'arrayIndexRange'; start: number; end: number };

function parsePathPart(part: string): PathSegment[] {
  if (part.endsWith('[]')) {
    return [
      { kind: 'key', name: part.slice(0, -2) },
      { kind: 'arrayAll' },
    ];
  }

  const rangeMatch = part.match(/^(.+)\[(\d+)-(\d+)\]$/);
  if (rangeMatch) {
    return [
      { kind: 'key', name: rangeMatch[1] },
      {
        kind: 'arrayIndexRange',
        start: Number(rangeMatch[2]),
        end: Number(rangeMatch[3]),
      },
    ];
  }

  if (part.endsWith('*')) {
    return [{ kind: 'keyWildcard', prefix: part.slice(0, -1) }];
  }

  return [{ kind: 'key', name: part }];
}

function parsePath(path: string): PathSegment[] {
  return path.split('.').flatMap(parsePathPart);
}

function matchesKeyWildcard(key: string, prefix: string): boolean {
  return key.startsWith(prefix);
}

function extractByPath(
  value: JSONSerializable,
  segments: PathSegment[],
  index: number
): JSONSerializable | undefined {
  if (index >= segments.length) {
    return value;
  }

  const segment = segments[index];
  const isLast = index === segments.length - 1;

  if (segment.kind === 'key') {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return undefined;
    }

    const child = (value as Record<string, JSONSerializable>)[segment.name];
    if (child === undefined) {
      return undefined;
    }

    if (isLast) {
      return { [segment.name]: child };
    }

    const nested = extractByPath(child, segments, index + 1);
    if (nested === undefined) {
      return undefined;
    }

    return { [segment.name]: nested };
  }

  if (segment.kind === 'keyWildcard') {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return undefined;
    }

    const result: Record<string, JSONSerializable> = {};

    Object.entries(value as Record<string, JSONSerializable>).forEach(
      ([key, child]) => {
        if (!matchesKeyWildcard(key, segment.prefix)) {
          return;
        }

        if (isLast) {
          result[key] = child;
          return;
        }

        const nested = extractByPath(child, segments, index + 1);
        if (nested !== undefined) {
          result[key] = nested;
        }
      }
    );

    return Object.keys(result).length > 0 ? result : undefined;
  }

  if (segment.kind === 'arrayAll') {
    if (!Array.isArray(value)) {
      return undefined;
    }

    const result: JSONSerializable[] = [];
    let any = false;

    value.forEach((item, itemIndex) => {
      if (isLast) {
        result[itemIndex] = item;
        any = true;
        return;
      }

      const nested = extractByPath(item, segments, index + 1);
      if (nested !== undefined) {
        result[itemIndex] = nested;
        any = true;
      }
    });

    return any ? result : undefined;
  }

  const arrayIndexRange = segment as Extract<
    PathSegment,
    { kind: 'arrayIndexRange' }
  >;
  if (!Array.isArray(value)) {
    return undefined;
  }

  const result: JSONSerializable[] = [];
  let any = false;

  for (
    let itemIndex = arrayIndexRange.start;
    itemIndex <= arrayIndexRange.end;
    itemIndex += 1
  ) {
    const item = value[itemIndex];
    if (item === undefined) {
      continue;
    }

    if (isLast) {
      result[itemIndex] = item;
      any = true;
      continue;
    }

    const nested = extractByPath(item, segments, index + 1);
    if (nested !== undefined) {
      result[itemIndex] = nested;
      any = true;
    }
  }

  return any ? result : undefined;
}

/**
 * Keep only whitelisted leaves from a JSON-serializable value.
 *
 * Path syntax examples:
 * - `xaxis.range`
 * - `yaxis*.range` (matches `yaxis`, `yaxis2`, …)
 * - `users[].addresses[].street`
 * - `items[0-2].id`
 */
export function filterJSONSerializableByPaths(
  value: JSONSerializable,
  allowedPaths: readonly string[]
): JSONSerializable {
  if (allowedPaths.length === 0) {
    return Array.isArray(value) ? [] : {};
  }

  let result: Record<string, JSONSerializable> = {};
  let hasResult = false;

  allowedPaths.forEach((path) => {
    const extracted = extractByPath(value, parsePath(path), 0);
    if (extracted === undefined) {
      return;
    }

    if (
      typeof extracted === 'object' &&
      extracted !== null &&
      !Array.isArray(extracted)
    ) {
      result = deepMerge(result, extracted);
      hasResult = true;
      return;
    }

    hasResult = true;
    result = deepMerge(result, { value: extracted });
  });

  return hasResult ? result : Array.isArray(value) ? [] : {};
}
